<template>
  <sui-segment attached="bottom">
    <sui-form>
      <h3 class="ui header">General settings</h3>

      <sui-form-fields>
        <sui-form-field class="shrink" :width="8">
          <label>Notifications count</label>
          <sui-input type="number" min="1" icon="list ol" v-model="notificationsCount" />
          <small>
            The maximum number of notifications on your screen.
          </small>
        </sui-form-field>
        <sui-form-field class="shrink" :width="8">
          <label>Notifications delay (sec)</label>
          <sui-input type="number" min="1" icon="stopwatch" v-model="notificationsDelay" />
          <small>
            How long notifications will be shown on your screen.
          </small>
        </sui-form-field>
        <sui-form-field class="no-shrink">
          <label>Notifications order</label>
          <sui-button-group class="fluid">
            <sui-button :class="{active: !notificationsOrder}" icon="sort numeric up" label-position="left" @click.prevent="notificationsOrder = 0">New under old</sui-button>
            <sui-button :class="{active: notificationsOrder}" icon="sort numeric up alternate" label-position="right" @click.prevent="notificationsOrder = 1">New above old</sui-button>
          </sui-button-group>
          <small>
            The place for new notifications.
          </small>
        </sui-form-field>
      </sui-form-fields>

      <sui-form-fields>
        <sui-form-field :width="16">
          <label>Check for updates</label>
          <sui-dropdown :options="updateIntervals" v-model="checkingUpdatesInterval" search selection />
          <small>
            Check if there are any fixes or new features for D2C Panel via downloading a small file version.json (~60 bytes).
          </small>
        </sui-form-field>
      </sui-form-fields>

      <sui-form-fields>
        <sui-form-field :width="6">
          <label>API server</label>
          <sui-input v-model="apiAddress" :readonly="!config.debug" :icon="isAPIok ? 'green power' : 'red power'" :loading="loadingStatus" />
          <small>
            The main API server serving all your requests.
          </small>
        </sui-form-field>
        <sui-form-field :width="6">
          <label>Websocket server</label>
          <sui-input v-model="wsAddress" :readonly="!config.debug" :icon="connected ? 'green power' : 'red power'" />
          <small>
            The event server sending all updates of your projects and hosts in real time.
          </small>
        </sui-form-field>
        <sui-form-field :width="4">
          <label>API timeout (sec)</label>
          <sui-input v-model.number="apiTimeout" icon="stopwatch" />
          <small>
            Maximum time for API requests.
          </small>
        </sui-form-field>
      </sui-form-fields>

      <sui-form-fields>
        <sui-form-field :width="16">
          <label>SSH access</label>
          <sui-message class="key" size="mini" attached="top">
            {{publicKeyValue || publicKeyError || `Loading failed!` }}<br>
            <a href="#" @click.prevent="updateKey()" v-show="publicKeyError">Try again</a>
          </sui-message>
          <sui-button class="mini" attached="bottom" :disabled="!publicKeyValue" fluid @click.prevent="copyKey($event)">
            {{publicKeyStatus}}
          </sui-button>
          <small>
            Add this key to your account or repository to allow D2C downloading the source code of your repositories.
          </small>
        </sui-form-field>
      </sui-form-fields>

      <h3 class="ui header">Experimental features</h3>
      <sui-form-field>
        <sui-checkbox v-model="colorCards" label="Color cards" />
        <small>Add a color background for service and host cards based on the colors of their logo.</small>
      </sui-form-field>
    </sui-form>
  </sui-segment>
</template>

<script>
  import {mapState, mapGetters} from 'vuex'
  import api from '@/api'
  import utils from '@/utils'

  export default {
    name: 'SettingsGeneralView',
    data() {
      return {
        isAPIok: true,
        loadingStatus: false,
        publicKeyValue: '',
        publicKeyStatus: 'Copy SSH Key',
        publicKeyError: null,
      }
    },
    computed: {
      ...mapState(['connected']),
      ...mapGetters(['config']),
      updateIntervals() {
        return [
          {value: 0, text: 'Never'},
          {value: 10 * 60 * 1000, text: 'Every 10 minutes'},
          {value: 30 * 60 * 1000, text: 'Every 30 minutes'},
          {value: 60 * 60 * 1000, text: 'Every hour'},
          {value: 6 * 60 * 60 * 1000, text: 'Every 6 hours'},
          {value: 24 * 60 * 60 * 1000, text: 'Every day'},
        ]
      },
      apiAddress: {
        get() { return this.config.apiAddress },
        set(value) { this.$store.dispatch('updateConfig', { apiAddress: value }) },
      },
      wsAddress: {
        get() { return this.config.wsAddress },
        set(value) { this.$store.dispatch('updateConfig', { wsAddress: value }) },
      },
      apiTimeout: {
        get() { return this.config.apiTimeout / 1000 },
        set(value) { this.$store.dispatch('updateConfig', { apiTimeout: Math.max(1, value) * 1000 }) },
      },
      notificationsCount: {
        get() { return this.config.notificationsCount },
        set(value) { this.$store.dispatch('updateConfig', { notificationsCount: Math.max(1, value) }) },
      },
      notificationsDelay: {
        get() { return this.config.notificationsDelay / 1000 },
        set(value) { this.$store.dispatch('updateConfig', { notificationsDelay: Math.max(1, value) * 1000 }) },
      },
      notificationsOrder: {
        get() { return this.config.notificationsOrder },
        set(value) { this.$store.dispatch('updateConfig', { notificationsOrder: value }) },
      },
      checkingUpdatesInterval: {
        get() { return this.config.checkingUpdatesInterval },
        set(value) { this.$store.dispatch('updateConfig', { checkingUpdates: Boolean(value),  checkingUpdatesInterval: value }) },
      },
      colorCards: {
        get() { return this.config.colorCards },
        set(value) { this.$store.dispatch('updateConfig', { colorCards: value }) },
      },
    },
    methods: {
      updateStatus() {
        this.loadingStatus = true
        api.getUserInfo()
          .then(() => this.isAPIok = true)
          .catch(() => this.isAPIok = false)
          .finally(() => this.loadingStatus = false)
      },
      updateKey() {
        this.publicKeyValue = ''
        this.publicKeyError = 'Loading...'
        utils.getPublicKey().then(key => {
          this.publicKeyError = key.error
          this.publicKeyValue = key.value
        })
      },
      copyKey() {
        this.$copyText(this.publicKeyValue).then(() => {
          this.publicKeyStatus = 'Copied!'
        }).catch(() => {
          this.publicKeyStatus = 'Can\'t copy!'
        })
        setTimeout(() => {
          this.publicKeyStatus = 'Copy SSH Key'
        }, 1000)
      },
    },
    mounted() {
      this.$options.interval = setInterval(this.updateStatus, 10000)
      this.updateStatus()
      this.updateKey()
    },
    beforeDestroy() {
      clearInterval(this.$options.interval)
    },
  }
</script>

<style scoped>
  small { margin: .3em 0; display: block; color: #aaa; }
  .no-shrink { flex-shrink: 0!important; }
  .shrink { flex-shrink: 1!important; }
  .key { margin-top: 0!important; line-break: anywhere; }
</style>
